import {
  IControl,
  IContainerControl,
  UIControl,
  SizeConstraint,
  MeasureParams,
  DrawParams,
  PdfSize,
  PdfRect,
  ArrangedChild,
} from '../../types';
import { registry } from '../registry';
import { getExplicitSize, drawTextClipped, measureText } from '../primitives';
import { PdfPen, PdfBrush, PdfColor, ETextAlign, PdfPoint, PdfUnit } from '../../../../pdf/pdf';

const DEFAULT_PEN = new PdfPen(PdfColor.fromRgb(0, 0, 0), 0.5);
const LIGHT_PEN = new PdfPen(PdfColor.fromRgb(160, 160, 160), 0.3);
const MARGIN = 3;
const ARROW_FILL = new PdfBrush(PdfColor.fromRgb(0, 0, 0));

export const MenuControl: IContainerControl = {
  type: 'Menu',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = control.children || [];
    const lineH = params.page?.measureString('Ag', params.font).height ?? 10;
    const menuH = lineH + MARGIN * 2;

    let totalWidth = 0;
    for (const child of children) {
      const size = registry.measure(child, { width: Number.MAX_VALUE, height: menuH }, params);
      totalWidth += size.width;
    }

    return new PdfSize(
      explicit.width ||
        Math.max(totalWidth, constraint.width === Number.MAX_VALUE ? totalWidth : constraint.width),
      explicit.height || menuH
    );
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = control.children || [];
    const results: ArrangedChild[] = [];
    let currentX = rect.x;

    for (const child of children) {
      const size = registry.measure(
        child,
        { width: Number.MAX_VALUE, height: rect.height },
        params
      );
      const childRect = new PdfRect(currentX, rect.y, size.width, rect.height);
      results.push({ child, rect: childRect });
      currentX += size.width;
    }

    return results;
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    // Menu bar: white background + bottom border line only
    params.page.drawRectangle(null, PdfBrush.White, rect);
    params.page.drawLine(
      DEFAULT_PEN,
      new PdfPoint(rect.x, rect.y + rect.height),
      new PdfPoint(rect.x + rect.width, rect.y + rect.height)
    );

    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(MenuControl);

export const MenuItemControl: IControl = {
  type: 'MenuItem',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const text = control.content || 'Item';
    const textSize = measureText(text, params.font, params.page);
    const hasChildren = (control.children || []).length > 0;
    const arrowW = hasChildren ? MARGIN * 3 : 0;

    return new PdfSize(
      explicit.width || Math.min(textSize.width + MARGIN * 4 + arrowW, constraint.width),
      explicit.height || constraint.height
    );
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    // Plain text, no border — hover/selected state not needed in wireframe
    if (control.content) {
      const textRect = new PdfRect(
        rect.x + MARGIN * 2,
        rect.y,
        rect.width - MARGIN * 4,
        rect.height
      );
      drawTextClipped(params.page, control.content, params.font, textRect, ETextAlign.CenterLeft);
    }

    // Submenu arrow indicator
    if ((control.children || []).length > 0) {
      const ax = rect.x + rect.width - MARGIN * 2;
      const ay = rect.y + rect.height / 2;
      const as_ = 2.5;
      params.page.drawPolygon(DEFAULT_PEN, ARROW_FILL, [
        new PdfPoint(ax - as_, ay - as_),
        new PdfPoint(ax + as_, ay),
        new PdfPoint(ax - as_, ay + as_),
      ]);
    }
  },
};

registry.register(MenuItemControl);

export const SeparatorControl: IControl = {
  type: 'Separator',

  measure(control: UIControl, constraint: SizeConstraint, _params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    return new PdfSize(
      explicit.width || constraint.width,
      explicit.height || PdfUnit.FromMillimeters(1.5)
    );
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const y = rect.y + rect.height / 2;
    params.page.drawLine(
      LIGHT_PEN,
      new PdfPoint(rect.x + MARGIN, y),
      new PdfPoint(rect.x + rect.width - MARGIN, y)
    );
  },
};

registry.register(SeparatorControl);

export const ComboBoxControl: IControl = {
  type: 'ComboBox',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const lineH = params.page?.measureString('Ag', params.font).height ?? 10;
    const text = control.content || '';
    const textWidth = text
      ? (params.page?.measureString(text, params.font).width ?? text.length * 4)
      : 0;

    const arrowZone = lineH + MARGIN * 2;
    const width =
      explicit.width ||
      Math.min(Math.max(textWidth + MARGIN * 4 + arrowZone, 80), constraint.width);
    const height = explicit.height || lineH + MARGIN * 2;

    return new PdfSize(Math.max(width, 80), Math.max(height, 14));
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    // Single outer border
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, rect);

    // Vertical divider before the arrow zone
    const arrowZoneW = rect.height;
    const divX = rect.x + rect.width - arrowZoneW;
    params.page.drawLine(
      DEFAULT_PEN,
      new PdfPoint(divX, rect.y + 1),
      new PdfPoint(divX, rect.y + rect.height - 1)
    );

    // Text
    if (control.content) {
      const textRect = new PdfRect(
        rect.x + MARGIN,
        rect.y,
        rect.width - arrowZoneW - MARGIN,
        rect.height
      );
      drawTextClipped(params.page, control.content, params.font, textRect, ETextAlign.CenterLeft);
    }

    // Down-arrow ▼
    const ax = divX + arrowZoneW / 2;
    const ay = rect.y + rect.height / 2;
    const as_ = 2.5;
    params.page.drawPolygon(DEFAULT_PEN, ARROW_FILL, [
      new PdfPoint(ax - as_, ay - as_ * 0.6),
      new PdfPoint(ax + as_, ay - as_ * 0.6),
      new PdfPoint(ax, ay + as_ * 0.8),
    ]);
  },
};

registry.register(ComboBoxControl);

export const ProgressBarControl: IControl = {
  type: 'ProgressBar',

  measure(control: UIControl, constraint: SizeConstraint, _params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    return new PdfSize(
      explicit.width || Math.min(100, constraint.width),
      explicit.height || PdfUnit.FromMillimeters(4)
    );
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    params.page.drawRectangle(DEFAULT_PEN, PdfBrush.White, rect);

    const value = parseFloat(control.attrs.Value || '50');
    const minimum = parseFloat(control.attrs.Minimum || '0');
    const maximum = parseFloat(control.attrs.Maximum || '100');
    const pct = Math.max(0, Math.min(1, (value - minimum) / (maximum - minimum)));

    if (pct > 0) {
      const pad = 1;
      const fillRect = new PdfRect(
        rect.x + pad,
        rect.y + pad,
        (rect.width - pad * 2) * pct,
        rect.height - pad * 2
      );
      params.page.drawRectangle(null, PdfBrush.Black, fillRect);
    }
  },
};

registry.register(ProgressBarControl);

export const SliderControl: IControl = {
  type: 'Slider',

  measure(control: UIControl, constraint: SizeConstraint, _params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    return new PdfSize(
      explicit.width || Math.min(100, constraint.width),
      explicit.height || PdfUnit.FromMillimeters(5)
    );
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const trackPad = 5;
    const trackY = rect.y + rect.height / 2;
    const trackX0 = rect.x + trackPad;
    const trackX1 = rect.x + rect.width - trackPad;

    // Track line
    params.page.drawLine(DEFAULT_PEN, new PdfPoint(trackX0, trackY), new PdfPoint(trackX1, trackY));

    // Thumb: small filled circle
    const value = parseFloat(control.attrs.Value || '50');
    const minimum = parseFloat(control.attrs.Minimum || '0');
    const maximum = parseFloat(control.attrs.Maximum || '100');
    const pct = Math.max(0, Math.min(1, (value - minimum) / (maximum - minimum)));

    const thumbX = trackX0 + (trackX1 - trackX0) * pct;
    const r = Math.min(rect.height / 2 - 1, 4);
    params.page.drawEllipse(
      DEFAULT_PEN,
      PdfBrush.White,
      new PdfRect(thumbX - r, trackY - r, r * 2, r * 2)
    );
  },
};

registry.register(SliderControl);

export const TabItemControl: IContainerControl = {
  type: 'TabItem',

  measure(control: UIControl, constraint: SizeConstraint, params: MeasureParams): PdfSize {
    const explicit = getExplicitSize(control);
    const children = control.children || [];

    let childWidth = 50,
      childHeight = 30;
    if (children.length > 0) {
      const size = registry.measure(children[0], constraint, params);
      childWidth = size.width;
      childHeight = size.height;
    }

    return new PdfSize(explicit.width || childWidth, explicit.height || childHeight);
  },

  arrange(control: UIControl, rect: PdfRect, params: DrawParams): ArrangedChild[] {
    const children = control.children || [];
    if (children.length === 0) return [];

    const childRect = new PdfRect(rect.x, rect.y, rect.width, rect.height);
    return [{ child: children[0], rect: childRect }];
  },

  draw(control: UIControl, rect: PdfRect, params: DrawParams): void {
    const arranged = this.arrange(control, rect, params);
    for (const { child, rect: childRect } of arranged) {
      registry.draw(child, childRect, params);
    }
  },
};

registry.register(TabItemControl);
